// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

// Role guard for console routes. Sits inside RequireAuth (which already ensures
// the user is signed in). RequireRole additionally checks that the user has the
// required operator rank.

import { type ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '@/context/auth-context'
import { RANK, isOperator } from '@/lib/access'
import type { Role } from '@/types'
import AppLoader from '@/components/AppLoader'
import AccessDenied from '@/components/AccessDenied'

interface RequireRoleProps {
  /** Minimum role required to view this route. */
  min: 'qh_admin' | 'platform_superadmin'
  children: ReactNode
}

export default function RequireRole({ min, children }: RequireRoleProps) {
  const { role, loading } = useAuth()

  if (loading) return <AppLoader />

  // User has sufficient rank — render the page.
  if (role && RANK[role] >= RANK[min as Role]) return <>{children}</>

  // Signed-in operator but below the required rank for this specific route
  // (e.g. qh_admin trying to reach /settings which requires platform_superadmin).
  // Redirect to their home rather than showing an error — no redirect loop risk
  // because /orgs only requires qh_admin+ which they already have.
  if (isOperator(role)) return <Navigate to="/orgs" replace />

  // Signed in but not an operator at all — show the access denied screen.
  return <AccessDenied />
}
